<template>
  <button
    :id="componentId"
    data-samora
    type="button"
    role="button"
    :class="[
      `sam-interactive-label is-${componentStyle}`,
      { 'is-loading': loading },
      { 'is-disabled': disabled },
      { 'is-inline': inline },
    ]"
    v-on="componentEvents"
  >
    <SamTooltip
      v-if="hasTooltip"
      class="sam-interactive-label__tooltip"
      :anchor-name="componentId"
      role="tooltip"
      v-bind="componentTooltip"
    />
    <span
      v-if="hasLabel"
      class="sam-interactive-label__text"
    >
      {{ componentLabel }}
    </span>
    <SamIcon
      v-if="icon"
      :class="`sam-interactive-label__icon is-position-${componentIconPosition}`"
      size="xs"
      :icon="icon"
      aria-hidden="true"
    />
  </button>
</template>

<script>
import SamTooltip from '../SamTooltip/SamTooltip.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import SAMORA_POSITIONS from '../../../utils/constants/samora-positions.enum';

export const INTERACTIVE_LABEL_STYLES = {
  PRIMARY: SAMORA_STYLES.PRIMARY,
  SECONDARY: SAMORA_STYLES.SECONDARY,
  SUCCESS: SAMORA_STYLES.SUCCESS,
  WARNING: SAMORA_STYLES.WARNING,
  DANGER: SAMORA_STYLES.DANGER,
};

export const INTERACTIVE_LABEL_ICON_POSITION = {
  START: SAMORA_POSITIONS.START,
  END: SAMORA_POSITIONS.END,
};

export default {
  name: 'SamInteractiveLabel',
  components: {
    SamTooltip,
    SamIcon,
  },
  props: {
    /**
     * The component's style (colors)
     * @values primary, secondary, success, warning, danger
     */
    samStyle: {
      type: String,
      required: false,
      default: 'primary',
      validator(type) {
        return Object.values(INTERACTIVE_LABEL_STYLES).includes(type);
      },
    },
    /**
     * The component's id
     */
    id: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * The component's value/text to be displayed
     */
    value: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The icon to be displayed in the label
     */
    icon: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * The icon's display position
     * @values start, end
     */
    iconPosition: {
      type: String,
      required: false,
      default: INTERACTIVE_LABEL_ICON_POSITION.END,
      validator(position) {
        return Object.values(INTERACTIVE_LABEL_ICON_POSITION).includes(position);
      },
    },
    /**
     * The component's tooltip message
     */
    tooltip: {
      type: [Object, String, Boolean],
      required: false,
      default: false,
    },
    /**
     * Whether to set the label in a loading state
     * @values true, false
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether to set the label in a disabled state
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether to display the label inline
     * @values true, false
     */
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    componentId() {
      // eslint-disable-next-line no-underscore-dangle
      return this.id || `interactiveLabel__${this._uid}`;
    },
    componentLabel() {
      if (this.loading) return '';
      return this.value;
    },
    componentIconPosition() {
      if (!Object.values(INTERACTIVE_LABEL_ICON_POSITION).includes(this.iconPosition))
        return INTERACTIVE_LABEL_ICON_POSITION.END;
      return this.iconPosition;
    },
    componentTooltip() {
      if (typeof this.tooltip === 'object') return this.tooltip;
      return {
        message: this.tooltip,
        position: 'top',
      };
    },
    componentStyle() {
      if (!Object.values(INTERACTIVE_LABEL_STYLES).includes(this.samStyle))
        return INTERACTIVE_LABEL_STYLES.PRIMARY;
      return this.samStyle;
    },
    componentEvents() {
      return !(this.disabled || this.loading) ? this.$listeners : {};
    },
    hasLabel() {
      return !!this.value;
    },
    hasTooltip() {
      return this.tooltip !== false;
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-interactive-label {
  @extend .d-flex, .items-center, .gap-1, .px-2;
  border-radius: $size--1;
  border: $size--0--fourth solid;
  height: $size--6;
  max-width: 100%;

  &__text {
    @extend .text--caption--demi;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    order: 1;
  }

  &__tooltip {
    // workaround for tooltip not messing with the flex gap layout
    position: absolute;
  }

  // DISPLAY
  &.is-inline {
    display: inline-flex;
  }

  // STYLES
  &.is-primary {
    border-color: $informative--border;
    color: $informative--main;
    background-color: $informative--surface;

    &:not(.is-disabled):hover {
      color: $informative--hover;
    }
  }

  &.is-secondary {
    border-color: $info-secondary--border;
    color: $info-secondary--main;
    background-color: $info-secondary--surface;

    &:not(.is-disabled):hover {
      color: $info-secondary--hover;
    }
  }

  &.is-danger {
    border-color: $error--border;
    color: $error--main;
    background-color: $error--surface;

    &:not(.is-disabled):hover {
      color: $error--hover;
    }
  }

  &.is-warning {
    border-color: $warning--border;
    color: $warning--main;
    background-color: $warning--surface;

    &:not(.is-disabled):hover {
      color: $warning--hover;
    }
  }

  &.is-success {
    border-color: $success--border;
    color: $success--main;
    background-color: $success--surface;

    &:not(.is-disabled):hover {
      color: $success--hover;
    }
  }

  &.is-loading,
  &.is-disabled {
    border-color: $neutral--30;
    color: $neutral--50;
    background-color: $neutral--20;
  }

  // STATES
  &:not(.is-loading):not(.is-disabled) {
    cursor: pointer;
  }

  &.is-loading {
    width: 124px;
    pointer-events: none;

    .sam-interactive-label__text {
      @include old-skeleton-blink;
      flex-grow: 1;
      height: $size--3;
    }

    .sam-interactive-label__icon {
      @extend .ml-auto;
    }
  }

  // ICON POSITION
  &__icon {
    &.is-position-start {
      order: 0;
    }
    &.is-position-end {
      order: 2;
    }
  }
}
</style>
